import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { timelineOfIncidentsOptions, timelineOfIncidentsOptionsDefault } from '../../../../helpers/filters/incidents';
import PeriodWidget from '../../components/scl-components/period-widget';
import Chart from '../../components/scl-components/stack-chart';
import RiskService from '../../../../services/RiskService';

const legendConfig = [
  {
    color: '#FF4D4F',
    label: 'Major Incidents',
    key: 'major',
    toggled: true,
  },
  {
    color: '#FADB14',
    label: 'Minor Incidents',
    key: 'minor',
    toggled: true,
  },
  {
    color: '#FA8C16',
    label: 'Moderate Incidents',
    key: 'moderate',
    toggled: true,
  },
  {
    color: '#52C41A',
    label: 'Corrective Actions',
    key: 'riskMitigationMeasures',
    toggled: true,
  },
];

const AssociatedEvents = ({
  loading: allLoading,
  // data: allData = [],
  isSingleSource = false,
  options = [],
  language = 'en',
  riskCategories = [],
  isOwnData = false,
  sourceId,
  subjectId,
}) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(timelineOfIncidentsOptionsDefault);
  const [riskType, setRiskType] = useState('all');
  const [legend, setLegend] = useState(legendConfig);
  const [loadingWidget, setLoadingWidget] = useState(true);
  const [allData, setAllData] = useState(true);
  const abort = useRef();

  useEffect(() => {
    const _fetch = async () => {
      setLoadingWidget(true);

      if (abort.current) {
        abort.current.abort();
      }

      const abortC = new AbortController();
      abort.current = abortC;

      const eventsRes = await RiskService.getEvents({
        id: subjectId,
        params: { sourceId },
        signal: abortC.signal,
      });

      if (eventsRes) {
        setAllData(eventsRes.data);
      }
      setLoadingWidget(false);
    };

    if (subjectId) {
      _fetch();
    }
  }, [sourceId, subjectId]);

  const loading = useMemo(() => allLoading || loadingWidget, [allLoading, loadingWidget]);

  const filtersConfig = useMemo(() => [
    {
      key: 'period',
      label: t('Period'),
      options: timelineOfIncidentsOptions.map((o) => ({
        ...o,
        label: t(o.label),
      })),
      defaultValue: timelineOfIncidentsOptionsDefault,
      onChange: (val) => setPeriod(val),
    },
    {
      key: 'riskTypes',
      label: t('Risk Type'),
      onChange: (val) => {
        setRiskType(val);
      },
      options: [
        {
          label: t('All risk types'),
          value: 'all',
        },
        ...riskCategories.map((o) => ({
          label: o.label[language],
          value: o.value,
          children: options.filter((O) => O.group === o.value).map((O) => ({
            value: O.value,
            label: O.label[language],
          }))
        }))
      ],
      defaultValue: 'all',
    },
  ], [t, riskCategories, options, language]);

  const toggleLegend = (key) => {
    setLegend((prev) => prev.map((c) => {
      if (c.key === key) {
        return { ...c, toggled: !c.toggled };
      }
      return c;
    }));
  };

  const data = useMemo(() => {
    if (Array.isArray(allData)) {
      const filteredAllData = allData.filter((a) => typeof a.incidentSeverity === 'string' || a.typeOfEvent === 'riskMitigationMeasures');
      if (riskType === 'all') {
        return filteredAllData;
      }
      return filteredAllData.filter((r) => (r.associatedRisk || []).includes(riskType));
    }
    return [];
  }, [allData, riskType]);

  const filteredByLegendData = useMemo(() => {
    const major = legend.find((l) => l.key === 'major').toggled;
    const moderate = legend.find((l) => l.key === 'moderate').toggled;
    const minor = legend.find((l) => l.key === 'minor').toggled;
    const riskMitigationMeasures = legend.find((l) => l.key === 'riskMitigationMeasures').toggled;

    return data.filter((d) => {
      if (!major) {
        if (d.incidentSeverity === 'major') {
          return false;
        }
      }

      if (!minor) {
        if (d.incidentSeverity === 'minor') {
          return false;
        }
      }

      if (!moderate) {
        if (d.incidentSeverity === 'moderate') {
          return false;
        }
      }

      if (!riskMitigationMeasures) {
        if (d.typeOfEvent === 'riskMitigationMeasures') {
          return false;
        }
      }

      return true;
    });
  }, [data, legend]);

  return (
    <PeriodWidget
      title={t('Associated Events')}
      filtersConfig={filtersConfig}
      loading={loading}
      className="custom-width-d with-border-header"
    >
      <Chart
        period={period}
        data={filteredByLegendData}
        type="INCIDENTS"
        isSingleSource={isSingleSource}
      />
      {isOwnData ? (
        <div className="legend-cont" style={{ marginTop: 42 }}>
          {legend.map((c) => (
            <div
              className={c.toggled ? 'item active' : 'item disabled'}
              key={`legend-${c.key}`}
              onClick={() => toggleLegend(c.key)}
            >
              <div className="cont">
                <div className="legend" style={{ background: c.color }} />
              </div>
              <div className="cont">
                <label>{c.label}</label>
              </div>
            </div>

          ))}
        </div>
      ) : (
        <div className="legend-cont is-grid" style={{ marginTop: 42 }}>
          {legend.map((c) => (
            <div
              className={c.toggled ? 'item active' : 'item disabled'}
              key={`legend-${c.key}`}
              onClick={() => toggleLegend(c.key)}
            >
              <div className="cont">
                <div className="legend" style={{ background: c.color }} />
              </div>
              <div className="cont">
                <label>{c.label}</label>
              </div>
            </div>
          ))}
        </div>
      )}
    </PeriodWidget>
  );
};

export default AssociatedEvents;
